import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  LinearProgress,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import React, { useRef, useState } from 'react';
import appReleasesService from '../services/app-releases-service';

const EMPTY_META = {
  versionCode: '',
  versionName: '',
  minSupportedVersionCode: '',
  isMandatory: false,
  releaseNotes: ''
};

/**
 * Upload form for a single platform. `platform` is 'android' or 'ios'.
 * The server reads version_code / version_name from the binary itself (APK
 * AndroidManifest.xml or IPA Info.plist); the fields here are optional
 * overrides and must match what is inside the file, so a typo cannot publish
 * a release whose version_code disagrees with the installed build number.
 */
function ReleaseDropzone({ platform, onUploadSuccess }) {
  const accept = platform === 'android' ? '.apk' : '.ipa';
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState(EMPTY_META);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(accept)) {
      setError(`Only ${accept} files are allowed for ${platform}.`);
      return;
    }
    setError(null);
    setFile(f);
  };

  const setField = (key) => (e) =>
    setMeta((m) => ({ ...m, [key]: e.target.value }));

  const canUpload = Boolean(file) && !uploading;

  const handleUpload = async () => {
    if (!canUpload) return;
    setError(null);
    setUploading(true);
    setProgress(0);
    try {
      const result = await appReleasesService.uploadRelease(
        platform,
        file,
        meta,
        (pct) => setProgress(pct)
      );
      if (result.success) {
        onUploadSuccess(result.data);
        setFile(null);
        setMeta(EMPTY_META);
        if (inputRef.current) inputRef.current.value = '';
      } else {
        setError(result.reason || 'Upload failed.');
      }
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <Box>
      <Box
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          pickFile(e.dataTransfer.files?.[0]);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onClick={() => !uploading && inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: uploading ? 'not-allowed' : 'pointer',
          bgcolor: isDragging ? 'primary.50' : 'grey.50'
        }}
      >
        {file ? (
          <>
            <InsertDriveFileIcon
              sx={{ fontSize: 40, color: 'primary.main', mb: 1 }}
            />
            <Typography variant="body1">{file.name}</Typography>
          </>
        ) : (
          <>
            <CloudUploadIcon
              sx={{ fontSize: 40, color: 'primary.main', mb: 1 }}
            />
            <Typography variant="body1">
              Drag &amp; drop a {accept} file, or click to choose
            </Typography>
          </>
        )}
        <Typography variant="caption" display="block" color="text.secondary">
          Up to 500MB
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Version code"
            fullWidth
            size="small"
            type="number"
            helperText="Read from the file if blank"
            value={meta.versionCode}
            onChange={setField('versionCode')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Version name"
            fullWidth
            size="small"
            placeholder="1.0.0"
            helperText="Read from the file if blank"
            value={meta.versionName}
            onChange={setField('versionName')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Min supported"
            fullWidth
            size="small"
            type="number"
            helperText="Force-update floor (optional)"
            value={meta.minSupportedVersionCode}
            onChange={setField('minSupportedVersionCode')}
          />
        </Grid>
        <Grid item xs={6} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={meta.isMandatory}
                onChange={(e) =>
                  setMeta((m) => ({ ...m, isMandatory: e.target.checked }))
                }
              />
            }
            label="Mandatory"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Release notes"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={meta.releaseNotes}
            onChange={setField('releaseNotes')}
          />
        </Grid>
      </Grid>

      {uploading && (
        <Box mt={2}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="text.secondary">
            {progress}% uploaded
          </Typography>
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error" mt={1}>
          {error}
        </Typography>
      )}

      <Box mt={2} textAlign="right">
        <Button
          variant="contained"
          disabled={!canUpload}
          onClick={handleUpload}
        >
          {uploading ? 'Uploading…' : `Upload ${platform} release`}
        </Button>
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => pickFile(e.target.files?.[0])}
      />
    </Box>
  );
}

export default ReleaseDropzone;
